import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { EmbeddedContentDefinition, Text } from 'modules/graphql';
import { getIsEditMode } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { Add16 } from 'ui/icons';
import { FormField } from 'ui/formField';
import { spacing } from 'ui/theme';
import { GhostSuccessButton } from 'ui/button';
import { DISPLAY_CONTENT } from '../../constants';
import { EmptyInput, ReadOnlyField, formatLabelForId, generateRandomString } from '../../utils';
import { CustomTextField } from './CustomTextField';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledFormField = styled(FormField)`
  align-items: baseline;
`;

const StyledAddIcon = styled(Add16)`
  margin-right: ${spacing.threeQuarter};
`;

const MAX_NUMBER_OF_FIELDS = 50;
const { customTextFieldsAddButtonLabel, customTextFieldsDefaultLabel, customTextFieldsHeader } =
  DISPLAY_CONTENT.settings;

export const CustomTextFields = () => {
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const { elements } = liveTemplate.mediumData as EmbeddedContentDefinition;
  const customFields = elements?.text || [];

  const setCustomFields = (updatedFields: Text[]) => {
    const newElements = {
      ...elements,
      text: updatedFields,
    };
    setCurrentMediumField('elements', newElements);
  };

  const handleAddField = () => {
    const label = customTextFieldsDefaultLabel;
    const id = `${formatLabelForId(label)}-${generateRandomString()}`;
    const newField = { id, label, text: '' };
    const updatedFieldsList = [...customFields];
    updatedFieldsList.push(newField);
    setCustomFields(updatedFieldsList);
  };

  const hasCustomFields = customFields?.length > 0;
  const emptyFieldsListLabel = !hasCustomFields ? customTextFieldsHeader : <></>;

  return (
    <Container>
      {customFields?.map((field, index) => (
        <CustomTextField key={`custom-text-field-${index}`} field={field} index={index} />
      ))}
      {isEditModeVisible && !isEmbeddedMessagingEnabled ? (
        <StyledFormField labelText={emptyFieldsListLabel} isHorizontalLayout>
          <GhostSuccessButton
            data-test="custom-text-field-add-button"
            id="custom-text-field-add-button"
            isDisabled={customFields?.length === MAX_NUMBER_OF_FIELDS}
            onClick={handleAddField}
          >
            <StyledAddIcon />
            {customTextFieldsAddButtonLabel}
          </GhostSuccessButton>
        </StyledFormField>
      ) : (
        !hasCustomFields && (
          <FormField labelText={customTextFieldsHeader} isHorizontalLayout>
            <ReadOnlyField>
              <EmptyInput />
            </ReadOnlyField>
          </FormField>
        )
      )}
    </Container>
  );
};
